import { createContext, useCallback, useState } from "react";
import { ProcessTrackerStack } from "@/components/ProcessTracker/ProcessTracker";
import { ProcessType } from "./processTypes";

export type StepStatus = "pending" | "processing" | "complete" | "error";

export interface ProcessStep {
  id: string;
  label: string;
  processingLabel?: string;
  completedLabel?: string;
  errorLabel?: string;
  status: StepStatus;
  errorMessage?: string;
}

export interface TrackedProcess {
  processId: string;
  type: ProcessType;
  title: string;
  steps: ProcessStep[];
  minimized: boolean;
  completionAction?: { label: string; to: string };
  errorAction?: { label: string; to: string };
}

export interface ProcessTrackerContextValue {
  processes: TrackedProcess[];
  startProcess: (
    type: ProcessType,
    title: string,
    steps: Pick<ProcessStep, "id" | "label" | "processingLabel" | "completedLabel" | "errorLabel">[],
    options?: { completionAction?: { label: string; to: string }; errorAction?: { label: string; to: string } }
  ) => string;
  updateStep: (processId: string, stepId: string, status: StepStatus, errorMessage?: string) => void;
  dismiss: (processId: string) => void;
  toggleMinimized: (processId: string) => void;
  isProcessTypeActive: (type: ProcessType) => boolean;
}

export const ProcessTrackerContext = createContext<ProcessTrackerContextValue | null>(null);

export default function ProcessTrackerProvider({ children }: { children: React.ReactNode }) {
  const [processes, setProcesses] = useState<TrackedProcess[]>([]);

  const startProcess = useCallback(
    (
      type: ProcessType,
      title: string,
      steps: Pick<ProcessStep, "id" | "label" | "processingLabel" | "completedLabel" | "errorLabel">[],
      options?: { completionAction?: { label: string; to: string }; errorAction?: { label: string; to: string } }
    ): string => {
      const processId = crypto.randomUUID();
      const trackedProcess: TrackedProcess = {
        processId,
        type,
        title,
        minimized: false,
        completionAction: options?.completionAction,
        errorAction: options?.errorAction,
        steps: steps.map((s) => ({ ...s, status: "pending" as const }))
      };
      setProcesses((prev) => [...prev, trackedProcess]);
      return processId;
    },
    []
  );

  const updateStep = useCallback((processId: string, stepId: string, status: StepStatus, errorMessage?: string) => {
    setProcesses((prev) =>
      prev.map((p) =>
        p.processId === processId
          ? {
              ...p,
              steps: p.steps.map((s) => (s.id === stepId ? { ...s, status, errorMessage } : s))
            }
          : p
      )
    );
  }, []);

  const dismiss = useCallback((processId: string) => {
    setProcesses((prev) => prev.filter((p) => p.processId !== processId));
  }, []);

  const toggleMinimized = useCallback((processId: string) => {
    setProcesses((prev) => prev.map((p) => (p.processId === processId ? { ...p, minimized: !p.minimized } : p)));
  }, []);

  const isProcessTypeActive = useCallback((type: ProcessType) => processes.some((p) => p.type === type), [processes]);

  return (
    <ProcessTrackerContext.Provider
      value={{ processes, startProcess, updateStep, dismiss, toggleMinimized, isProcessTypeActive }}
    >
      {children}
      {processes.length > 0 && (
        <ProcessTrackerStack processes={processes} onDismiss={dismiss} onToggleMinimized={toggleMinimized} />
      )}
    </ProcessTrackerContext.Provider>
  );
}
